import { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { api } from '@shared/services/api';
import { useAdaptivePoll } from '@shared/hooks/useAdaptivePoll';

export interface UserNotification {
  id: string;
  user_id: string;
  organization_id: string;
  type: string;
  title: string;
  message: string;
  is_read: boolean;
  action_url?: string;
  metadata?: Record<string, any>;
  created_at: string;
  read_at?: string;
}

export interface NotificationListResponse {
  notifications: UserNotification[];
  unread_count: number;
  total_count: number;
}

interface NotificationContextType {
  notifications: UserNotification[];
  unreadCount: number;
  isLoading: boolean;
  error: string | null;
  fetchNotifications: (unreadOnly?: boolean) => Promise<void>;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  clearNotifications: (readOnly?: boolean) => Promise<void>;
  deleteNotification: (id: string) => Promise<void>;
  refreshUnreadCount: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}

interface NotificationProviderProps {
  children: ReactNode;
}

export function NotificationProvider({ children }: NotificationProviderProps) {
  const [notifications, setNotifications] = useState<UserNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Read inside the adaptive poll's fetcher so the poll is not recreated on
  // every response.
  const unreadCountRef = useRef(0);
  const refreshUnreadCountRef = useRef<() => Promise<void>>(async () => {});

  const fetchNotifications = async (unreadOnly = false) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.get<NotificationListResponse>(
        `/user-notifications${unreadOnly ? '?unreadOnly=true' : ''}`
      );
      setNotifications(response.data.notifications);
      setUnreadCount(response.data.unread_count);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch notifications');
      console.error('Error fetching notifications:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const markAsRead = async (id: string) => {
    try {
      await api.put(`/user-notifications/${id}`);
      // Update local state
      setNotifications((prev) =>
        prev.map((n) =>
          n.id === id ? { ...n, is_read: true, read_at: new Date().toISOString() } : n
        )
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (err: any) {
      console.error('Error marking notification as read:', err);
      throw err;
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.post('/user-notifications/mark-all-read');
      // Update local state
      setNotifications((prev) =>
        prev.map((n) => ({ ...n, is_read: true, read_at: new Date().toISOString() }))
      );
      setUnreadCount(0);
    } catch (err: any) {
      console.error('Error marking all as read:', err);
      throw err;
    }
  };

  const clearNotifications = async (readOnly = true) => {
    try {
      await api.delete(`/user-notifications/clear?read_only=${readOnly}`);
      if (readOnly) {
        // Only clear read notifications
        setNotifications((prev) => prev.filter((n) => !n.is_read));
      } else {
        // Clear all
        setNotifications([]);
        setUnreadCount(0);
      }
    } catch (err: any) {
      console.error('Error clearing notifications:', err);
      throw err;
    }
  };

  const deleteNotification = async (id: string) => {
    try {
      await api.delete(`/user-notifications/${id}`);
      // Update local state
      const notification = notifications.find((n) => n.id === id);
      setNotifications((prev) => prev.filter((n) => n.id !== id));
      if (notification && !notification.is_read) {
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }
    } catch (err: any) {
      console.error('Error deleting notification:', err);
      throw err;
    }
  };

  const refreshUnreadCount = async () => {
    try {
      const response = await api.get<{ unread_count: number }>('/user-notifications/unread-count');
      setUnreadCount(response.data.unread_count);
    } catch (err: any) {
      console.error('Error refreshing unread count:', err);
    }
  };

  // Keep the poll's refs pointing at the latest values. The unread count is
  // fetched by the poll's own initial tick, so it is not duplicated here.
  unreadCountRef.current = unreadCount;
  refreshUnreadCountRef.current = refreshUnreadCount;
  useEffect(() => {
    fetchNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initial fetch, then a visibility-aware poll for the unread badge. Adaptive:
  // 60s base backing off to 5min while the count is unchanged, and paused while
  // the tab is hidden. The previous fixed 30s interval ran forever in every
  // background tab — measured as the single largest source of API traffic.
  useAdaptivePoll({
    enabled: true,
    baseIntervalMs: 60000,
    maxIntervalMs: 300000,
    fetcher: async () => {
      await refreshUnreadCountRef.current();
      return String(unreadCountRef.current);
    },
    isEqual: (a, b) => a === b,
  });

  const value: NotificationContextType = {
    notifications,
    unreadCount,
    isLoading,
    error,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    clearNotifications,
    deleteNotification,
    refreshUnreadCount,
  };

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
}
